import { useState, useRef, useEffect } from 'react'
import { Link, NavLink, Outlet, useLocation } from 'react-router-dom'
import { useMe } from '../hooks/useMe'
import { useBreadcrumb } from '../hooks/useBreadcrumb'
import { Icon, type IconName } from './Icon'
import { NewEventModal } from './NewEventModal'
import { useFlags, clearFlags } from '../lib/featureFlags'

// AppShell - sidebar nav + topbar + content outlet. Matches the prototype's
// Heritage Operations Centre layout (sidebar gradient, brand block, topbar
// with breadcrumb and icon-button cluster). At <=920px the sidebar collapses
// to a 60px icon rail via CSS rules in src/index.css.
//
// Topbar bell / search / quick-add are stubbed for v1 - the buttons open
// placeholder popovers that future specs (05 notifications, 14 command
// palette) will wire up properly. New event is fully wired because we
// already have NewEventModal.

type NavSpec = {
  to: string
  label: string
  icon: IconName
  adminOnly?: boolean
  // `comingSoon` = the feature behind this link isn't built yet, OR the
  // page exists only as a placeholder/stub. Visible in the sidebar so
  // Chris can see the shape of the MVP at a glance; rendered in muted
  // colour with a "soon" badge so it's obvious which entries are live.
  comingSoon?: boolean
}

type NavSection = {
  // Optional small uppercase heading above the section (e.g. "Libraries").
  // First section has no heading — it's the main "operate" group.
  heading?: string
  items: NavSpec[]
}

const NAV_SECTIONS: NavSection[] = [
  {
    items: [
      { to: '/',            label: 'Dashboard',          icon: 'home' },
      { to: '/events',      label: 'Events',             icon: 'list-tree' },
      { to: '/calendar',    label: 'Calendar',           icon: 'calendar' },
      { to: '/my-actions',  label: 'My Actions & Tasks', icon: 'flag' },
      { to: '/reports',     label: 'Reports',            icon: 'bar-chart' },
      { to: '/flags',       label: 'Flags',              icon: 'flag' },
      // Forecast Studio — model a candidate event (Idea) for viability before
      // committing it to the plan. Admin-gated (SG-Events-Admins).
      { to: '/forecast',    label: 'Forecast Studio',    icon: 'target', adminOnly: true },
      { to: '/claude-help', label: 'Claude Help',        icon: 'info', adminOnly: true }
    ]
  },
  {
    heading: 'Libraries',
    items: [
      // Activities / Components / Risks are intended to live as org-level
      // libraries (manage once, reuse across events). Activities and
      // Components shipped in PRs A/B (migs 034/035). Resources Directory
      // is still pending its admin surface.
      // Activities library shipped in PR B (mig 035). Admin manages the
      // org-level catalog from here; auto-add rows feed new events and
      // defaults snapshot into the per-event values at copy time.
      { to: '/resources/activities',      label: 'Activities',          icon: 'calendar' },
      // Components library shipped in PR A (mig 034). Admin can manage
      // the org-level catalog from here; auto-add rows feed new events.
      { to: '/resources/components',      label: 'Components',          icon: 'box' },
      { to: '/resources/risks',           label: 'Risks',               icon: 'alert-triangle' },
      // Roles library shipped in Roster rebuild PR R1 (mig 039).
      // Drives the per-event Roster tab's role catalogue.
      { to: '/resources/roles',           label: 'Roles',               icon: 'users' },
      // Core NYMR Functions — reusable delivery functions pulled into forecast
      // teams (mig 072). Admin-managed catalogue.
      { to: '/resources/functions',       label: 'Functions',           icon: 'book-open', adminOnly: true },
      // Ticket-type catalogue (Feature 4, mig 064). Admin manages the
      // standard set event managers pick from on each Ticketing tab.
      { to: '/resources/ticket-types',    label: 'Ticket Types',        icon: 'ticket', adminOnly: true },
      { to: '/admin/templates/briefing-topics', label: 'Briefing Topics', icon: 'info' },
      { to: '/admin/checklist-templates', label: 'Checklist Templates', icon: 'check-circle' },
      // /resources — ResourcesDirectory (spec 46). Cross-event matrix of
      // the 6 EventResources categories per non-archived event. Lands
      // with the L1 backend (mig 048) populated; click any cell to
      // jump into the per-event ResourcesPanel at that tab.
      { to: '/resources',                 label: 'Resources Directory', icon: 'grid' }
    ]
  },
  {
    items: [
      { to: '/admin',     label: 'Admin',         icon: 'shield-check', adminOnly: true },
      // Tweak T2a — archived events listing. Admin-only. Sits below
      // /admin so admin-scoped surfaces cluster together visually.
      { to: '/archive',   label: 'Archive',       icon: 'box',          adminOnly: true }
    ]
  }
]

export function AppShell() {
  const { data: me } = useMe()
  const initials = avatarInitials(me?.name ?? me?.upn ?? '?')

  return (
    <div className="min-h-screen flex" style={{ background: '#f5f1ea' }}>
      <aside
        data-shell="sidebar"
        className="w-60 text-parchment flex flex-col flex-shrink-0"
        style={{ background: 'linear-gradient(180deg, #14322a 0%, #0a4a3a 100%)', borderRight: '1px solid #0a4a3a' }}
      >
        <div
          className="px-5 py-5"
          style={{ borderBottom: '1px solid rgba(245, 241, 234, 0.10)' }}
          data-shell-hide="narrow"
        >
          <div className="font-display text-2xl font-bold leading-tight">
            NYMR<span style={{ color: '#c9a13a' }}>.</span>
          </div>
          <div
            className="mt-1.5 text-[10px] font-semibold uppercase"
            style={{ letterSpacing: '0.14em', color: 'rgba(245, 241, 234, 0.55)' }}
          >
            Events Operations Centre
          </div>
        </div>
        <nav className="flex-1 px-3 py-4 overflow-y-auto">
          {NAV_SECTIONS.map((section, idx) => {
            const items = section.items.filter((n) => !n.adminOnly || me?.isAdmin)
            if (items.length === 0) return null
            return (
              <div key={idx} className={idx === 0 ? '' : 'mt-4 pt-3 border-t border-parchment/10'}>
                {section.heading && (
                  <div
                    className="text-[10px] font-semibold uppercase px-3 mb-1.5"
                    style={{ letterSpacing: '0.14em', color: 'rgba(245, 241, 234, 0.40)' }}
                    data-shell-hide="narrow"
                  >
                    {section.heading}
                  </div>
                )}
                {items.map((n) => (
                  <NavLink
                    key={n.to}
                    to={n.to}
                    end={n.to === '/'}
                    data-shell-nav-item
                    className={({ isActive }) =>
                      'flex items-center gap-2.5 px-3 py-2 rounded-md text-sm transition-colors border-l-2 mb-px ' +
                      (isActive
                        ? (n.comingSoon
                            ? 'text-parchment/75 font-semibold border-brass/60'
                            : 'text-parchment font-semibold border-brass')
                        : (n.comingSoon
                            ? 'border-transparent text-parchment/45 hover:bg-parchment/5 hover:text-parchment/70'
                            : 'border-transparent text-parchment/75 hover:bg-parchment/5 hover:text-parchment'))
                    }
                    style={({ isActive }) =>
                      isActive ? { background: 'rgba(245, 241, 234, 0.10)' } : undefined
                    }
                  >
                    <Icon name={n.icon} size={17} />
                    <span className="flex-1" data-shell-hide="narrow">{n.label}</span>
                    {n.comingSoon && (
                      <span
                        className="text-[9px] font-mono uppercase tracking-wider px-1.5 py-0.5 rounded"
                        style={{ background: 'rgba(245, 241, 234, 0.08)', color: 'rgba(245, 241, 234, 0.55)' }}
                        data-shell-hide="narrow"
                      >
                        soon
                      </span>
                    )}
                  </NavLink>
                ))}
              </div>
            )
          })}
        </nav>
        <div
          className="px-4 py-3 text-[11px] font-mono"
          style={{
            borderTop: '1px solid rgba(245, 241, 234, 0.10)',
            background: 'rgba(0, 0, 0, 0.15)',
            color: 'rgba(245, 241, 234, 0.5)'
          }}
          data-shell-hide="narrow"
        >
          v0.2 · phase 2
        </div>
      </aside>

      <main className="flex-1 flex flex-col min-w-0">
        <Topbar me={me ?? null} initials={initials} />
        <div data-shell="content" className="flex-1 overflow-y-auto px-8 pt-7 pb-14">
          <Outlet />
        </div>
      </main>
    </div>
  )
}

// ---- Topbar ---------------------------------------------------------------

function Topbar({ me, initials }: { me: ReturnType<typeof useMe>['data'] | null; initials: string }) {
  const { trail } = useBreadcrumb()
  const { pathname } = useLocation()
  const [now, setNow] = useState(() => new Date())
  const [openMenu, setOpenMenu] = useState<null | 'search' | 'bell' | 'plus'>(null)
  const [newEventOpen, setNewEventOpen] = useState(false)

  // Live clock - refreshes once a minute, matches the topbar in the
  // prototype. setInterval is fine here; the shell is mounted for the
  // lifetime of the session.
  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 60_000)
    return () => clearInterval(t)
  }, [])

  return (
    <header
      data-shell="topbar"
      className="h-16 px-7 flex items-center justify-between flex-shrink-0 bg-white"
      style={{ borderBottom: '1px solid #d8d2c4' }}
    >
      {/* Breadcrumb */}
      <nav aria-label="Breadcrumb" className="flex items-center gap-2 text-[13.5px] text-ink-soft min-w-0">
        {trail.map((c, i) => {
          const last = i === trail.length - 1
          return (
            <span key={`${c.label}-${i}`} className="flex items-center gap-2 min-w-0">
              {c.to && !last ? (
                <Link to={c.to} className="hover:text-green transition-colors truncate">
                  {c.label}
                </Link>
              ) : (
                <span className={last ? 'text-ink font-semibold truncate' : 'truncate'}>
                  {c.label}
                </span>
              )}
              {!last && <Icon name="chevronRight" size={13} className="text-line flex-shrink-0" />}
            </span>
          )
        })}
      </nav>

      {/* Actions */}
      <div className="flex items-center gap-2.5 flex-shrink-0">
        <FeatureFlagIndicator />

        <div
          className="hidden md:flex items-center gap-1.5 text-xs text-ink-soft font-mono mr-1"
          data-shell-hide="narrow"
        >
          <Icon name="clock" size={13} />
          <span>
            {now.toLocaleString('en-GB', {
              weekday: 'short',
              day: 'numeric',
              month: 'short',
              hour: '2-digit',
              minute: '2-digit'
            })}
          </span>
        </div>

        <IconButton
          label="Search (coming soon)"
          icon="search"
          onClick={() => setOpenMenu((m) => (m === 'search' ? null : 'search'))}
        />
        <IconButton
          label="Notifications"
          icon="bell"
          hasDot
          onClick={() => setOpenMenu((m) => (m === 'bell' ? null : 'bell'))}
        />
        <IconButton
          label="Quick add"
          icon="plus"
          onClick={() => setOpenMenu((m) => (m === 'plus' ? null : 'plus'))}
        />

        <div className="flex items-center gap-2.5 pl-1.5 pr-3 py-1 rounded-full border border-line bg-parchment">
          <div
            className="w-7 h-7 rounded-full text-white text-xs font-bold flex items-center justify-center"
            style={{ background: 'linear-gradient(135deg, #9b7f2a 0%, #c9a13a 100%)' }}
          >
            {initials}
          </div>
          {me && (
            <div className="text-[13px] font-semibold leading-tight" data-shell-hide="narrow">
              {me.name ?? me.upn}
            </div>
          )}
        </div>

        <a
          href="/.auth/logout"
          className="text-xs text-ink-soft hover:text-oxblood transition-colors"
          data-shell-hide="narrow"
        >
          Sign out
        </a>
      </div>

      {/* Floating menus / popovers */}
      {openMenu === 'search' && (
        <StubPopover onClose={() => setOpenMenu(null)} alignRight={140}>
          <div className="text-sm font-semibold mb-1">Quick search</div>
          <p className="text-xs text-ink-soft">
            A command palette is coming in a later phase. For now use the
            events list or the search box on each tab.
          </p>
        </StubPopover>
      )}
      {openMenu === 'bell' && (
        <StubPopover onClose={() => setOpenMenu(null)} alignRight={100}>
          <div className="text-sm font-semibold mb-1">Notifications</div>
          <p className="text-xs text-ink-soft">
            No new alerts. Notifications wire up once the Briefings &amp;
            Comms feature ships.
          </p>
        </StubPopover>
      )}
      {openMenu === 'plus' && (
        <QuickAddMenu
          pathname={pathname}
          onClose={() => setOpenMenu(null)}
          onCreateEvent={() => {
            setOpenMenu(null)
            setNewEventOpen(true)
          }}
        />
      )}

      {newEventOpen && <NewEventModal onClose={() => setNewEventOpen(false)} />}
    </header>
  )
}

// ---- v2 feature-flag indicator --------------------------------------------
//
// Renders a small pill listing active v2 flags. Click → clears all flags
// (sessionStorage purge, immediate re-render). Hidden when no flags are
// active. Visible to everyone for v2.0 — when v2 graduates, swap to a
// dev-only check.

function FeatureFlagIndicator() {
  const flags = useFlags()
  const active = Object.keys(flags).filter((k) => flags[k])
  if (active.length === 0) return null
  const label = `v2 flags active: ${active.join(', ')}`
  return (
    <button
      type="button"
      onClick={clearFlags}
      title={`${label} — click to clear`}
      aria-label={`${label}, click to clear`}
      className="hidden md:flex items-center gap-1.5 text-[11px] font-mono uppercase tracking-wide text-green border border-green/50 rounded px-2 py-0.5 hover:bg-green/10 transition-colors"
      data-shell-hide="narrow"
    >
      <span className="w-1.5 h-1.5 rounded-full bg-green" aria-hidden />
      <span>v2: {active.join(', ')}</span>
    </button>
  )
}

// ---- Icon button (topbar) -------------------------------------------------

function IconButton({
  icon, label, hasDot, onClick
}: {
  icon: IconName
  label: string
  hasDot?: boolean
  onClick: () => void
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={label}
      title={label}
      className="relative w-9 h-9 rounded-md border border-line bg-white text-ink-soft hover:bg-parchment hover:text-ink transition-colors flex items-center justify-center"
    >
      <Icon name={icon} size={16} />
      {hasDot && (
        <span
          aria-hidden
          className="absolute top-1.5 right-1.5 w-2 h-2 rounded-full bg-oxblood"
          style={{ border: '2px solid #fff' }}
        />
      )}
    </button>
  )
}

// ---- Stub popover ---------------------------------------------------------

// Tiny click-outside popover, used for Search and Notifications until the
// real features land. The `alignRight` prop positions the popover relative
// to the topbar's right edge so it sits under the icon button that opened
// it (rough alignment is fine for stubs).
function StubPopover({
  children, onClose, alignRight
}: {
  children: React.ReactNode
  onClose: () => void
  alignRight: number
}) {
  const ref = useRef<HTMLDivElement>(null)
  useEffect(() => {
    const onDoc = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) onClose()
    }
    // Defer registration so the click that opened the popover doesn't close it.
    const t = setTimeout(() => document.addEventListener('mousedown', onDoc), 0)
    return () => {
      clearTimeout(t)
      document.removeEventListener('mousedown', onDoc)
    }
  }, [onClose])
  return (
    <div
      ref={ref}
      role="dialog"
      className="absolute top-14 z-30 w-72 bg-white border border-line rounded-lg shadow-lg p-4"
      style={{ right: alignRight }}
    >
      {children}
    </div>
  )
}

// ---- Quick add menu -------------------------------------------------------

function QuickAddMenu({
  pathname, onClose, onCreateEvent
}: {
  pathname: string
  onClose: () => void
  onCreateEvent: () => void
}) {
  const ref = useRef<HTMLDivElement>(null)
  useEffect(() => {
    const onDoc = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) onClose()
    }
    const t = setTimeout(() => document.addEventListener('mousedown', onDoc), 0)
    return () => {
      clearTimeout(t)
      document.removeEventListener('mousedown', onDoc)
    }
  }, [onClose])

  const onEventDetail = /^\/events\/\d+/.test(pathname)

  return (
    <div
      ref={ref}
      role="menu"
      className="absolute top-14 z-30 w-56 bg-white border border-line rounded-lg shadow-lg overflow-hidden"
      style={{ right: 60 }}
    >
      <button
        role="menuitem"
        type="button"
        onClick={onCreateEvent}
        className="w-full flex items-center gap-2.5 px-3 py-2.5 text-sm text-left hover:bg-parchment transition-colors"
      >
        <Icon name="plus" size={14} className="text-green" />
        <span>New event</span>
      </button>
      {onEventDetail && (
        <button
          role="menuitem"
          type="button"
          disabled
          title="Use the Activities tab on this event to add an activity."
          className="w-full flex items-center gap-2.5 px-3 py-2.5 text-sm text-left text-ink-soft opacity-60 cursor-not-allowed border-t border-line"
        >
          <Icon name="plus" size={14} />
          <span>New activity</span>
        </button>
      )}
    </div>
  )
}

// ---- Helpers --------------------------------------------------------------

function avatarInitials(full: string): string {
  const parts = full.trim().split(/\s+/).filter(Boolean)
  if (parts.length === 0) return '?'
  if (parts.length === 1) return parts[0]!.slice(0, 2).toUpperCase()
  return (parts[0]![0]! + parts[1]![0]!).toUpperCase()
}
